import { useCallback, useRef } from 'react';
import ServiceManager from 'services/ServiceManager';
import { POLLING_STATUS } from 'components/DocBox/DocBox.types';

export type HeartBeatData = { status?: string; jsonResult?: string };

export type PollHandlers = {
    /** Side effects to run for a terminal heartBeat status (success / error / notFound). */
    onResult: (data: HeartBeatData) => void;
    /** Called when the heartBeat request itself fails with HTTP 404 and a response body. */
    onHttp404: (body: unknown) => void;
    /** Called after MAX_CONSECUTIVE_ERRORS consecutive request failures. */
    onGiveUp: () => void;
};

const TERMINAL_STATUSES = [POLLING_STATUS.success, POLLING_STATUS.error, POLLING_STATUS.notFound];
const POLL_INTERVAL_MS = 2500;
const MAX_CONSECUTIVE_ERRORS = 5;

/**
 * Polls the `heartBeat` endpoint every {@link POLL_INTERVAL_MS} until a terminal status arrives.
 *
 * Resolves when the heartBeat responds with a terminal status (success / error / notFound) — the
 * caller reacts to which one via `onResult`. Rejects only on transport failures: an HTTP 404 with a
 * body, or {@link MAX_CONSECUTIVE_ERRORS} consecutive request errors. This mirrors the previous
 * inline polling behaviour exactly. The error counter is per-hook-instance (was a module global).
 */
export const usePollSubmissionStatus = () => {
    const errorCounterRef = useRef(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const stop = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        errorCounterRef.current = 0;
    }, []);

    const poll = useCallback((identifier: string, handlers: PollHandlers) => new Promise<void>((resolve, reject) => {
        const tick = (): ReturnType<typeof setTimeout> => setTimeout(async () => {
            try {
                const { data } = await (ServiceManager.customService('heartBeat', [{ identifier }]) as Promise<{ data: HeartBeatData }>);

                if ((TERMINAL_STATUSES as string[]).includes(data?.status ?? '')) {
                    handlers.onResult(data);
                    stop();
                    resolve();
                } else {
                    timeoutRef.current = tick();
                }
            } catch (err: any) {
                if (err?.status === 404 && err?.response?.data) {
                    handlers.onHttp404(err.response.data);
                    stop();
                    reject(err);

                    return;
                }

                errorCounterRef.current += 1;
                if (errorCounterRef.current >= MAX_CONSECUTIVE_ERRORS) {
                    stop();
                    handlers.onGiveUp();
                    reject(err);
                }

                // eslint-disable-next-line no-console
                console.error(err);
            }
        }, POLL_INTERVAL_MS);

        timeoutRef.current = tick();
    }), [stop]);

    return { poll, stop };
};
